import NavigationBar from "@/components/studentNavigationBar";
import ViewAsStudentBanner from "@/components/view-as-student-banner";
import { Outlet } from "react-router-dom";
import { AuthGuard } from "@/libs/guard/auth-guard";
import { ROLES } from "@/libs/constants/roles";
import ThemeProvider from "@/libs/providers/theme-provider";
import { useAppSelector } from "@quest/hooks";
import { useStudentDetailsByIdpId } from "@/libs/hooks/use-user";
import { useViewAsPreview } from "@/libs/hooks/use-view-as-preview";
import Loader from "@quest/ui/components/loader";
import { useAppDispatch } from "@quest/hooks";
import { setStudentDetails } from "@quest/slice/students/student.slice";
import { useEffect, useState } from "react";
import { useCheckStudentIcaresTestStatus } from "@/shared/assignments/hooks";
import CModal from "@quest/ui/components/custom-modal";
import { useTranslation } from "react-i18next";
import IcaresPopup from "@/shared/onboarding/icares-popup";
import OnboardingTutorial from "@/shared/onboarding/index";
import ScrollToTop from "@quest/ui/components/scroll-to-top";
import { toast } from "react-toastify";
import { useSubscribe } from "@/libs/hooks/use-subscribe";
import AllowNotifications from "@/components/allow-notifications";
import { useQueryClient } from "@tanstack/react-query";
import { refreshNotificationsCache } from "@/shared/announcements/hooks";

function StudentLayout() {
  const { role } = useAppSelector((state) => state.auth);

  return (
    <AuthGuard
      requiredRoles={[ROLES.STUDENT]}
      redirectTo={role === ROLES.PARENT ? "/parents/passport" : "/admin"}
    >
      <ThemeProvider>
        <StudentLayoutContent />
      </ThemeProvider>
    </AuthGuard>
  );
}

function StudentLayoutContent() {
  const [openIcaresPopup, setOpenIcaresPopup] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [notificationPermissionPopup, setNotificationPermissionPopup] =
    useState<boolean>(false);

  const { idpId, role, has_skipped_tutorial, studentId } = useAppSelector(
    (state) => state.auth,
  );
  const { isPreviewMode, idpId: previewIdpId } = useViewAsPreview();

  const dispatch = useAppDispatch();

  const { t } = useTranslation("New");

  const { t: tCommon } = useTranslation("Common");

  const effectiveIdpId = isPreviewMode ? previewIdpId : idpId;
  const shouldLoadStudentDetails = isPreviewMode || role === ROLES.STUDENT;

  const {
    data: studentDetails,
    isLoading: isStudentDetailsLoading,
    isError: isStudentDetailsError,
    error: studentDetailsError,
  } = useStudentDetailsByIdpId(effectiveIdpId, shouldLoadStudentDetails);

  const {
    data: icaresTestStatus,
    isLoading: isIcaresTestStatusLoading,
    isError: isIcaresTestStatusError,
    error: icaresTestStatusError,
  } = useCheckStudentIcaresTestStatus(
    isPreviewMode ? null : studentDetails?.data?.id || null,
    isPreviewMode ? null : studentDetails?.data?.schoolYear || null,
  );

  useEffect(() => {
    if (isPreviewMode) {
      setOpenIcaresPopup(false);
      return;
    }
    if (icaresTestStatus?.data === false) {
      setOpenIcaresPopup(true);
    }
  }, [icaresTestStatus, isPreviewMode]);

  useEffect(() => {
    if (studentDetails) {
      dispatch(
        setStudentDetails({
          studentId: studentDetails?.data?.id?.toString() || "",
          fullName: studentDetails?.data?.fullName || "",
          studentEmail: studentDetails?.data?.studentEmail || "",
          schoolYear: studentDetails?.data?.schoolYear || 0,
          rollNumber: studentDetails?.data?.studentId || "",
          educationLevel: studentDetails?.data?.educationLevel || "",
        }),
      );
    }
  }, [studentDetails]);

  const queryClient = useQueryClient();

  // notifications

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      // Register service worker immediately, don't wait for load event
      const registerServiceWorker = async () => {
        try {
          // Check if we're on HTTPS or localhost
          if (
            location.protocol === "https:" ||
            location.hostname === "localhost" ||
            location.hostname === "127.0.0.1"
          ) {
            const registration = await navigator.serviceWorker.register(
              "/service-worker.js",
              {
                scope: "/",
              },
            );

            // Check for updates
            registration.addEventListener("updatefound", () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener("statechange", () => {
                  if (
                    newWorker.state === "installed" &&
                    navigator.serviceWorker.controller
                  ) {
                    console.log("New service worker available");
                  }
                });
              }
            });

            // Handle updates
            let refreshing = false;
            navigator.serviceWorker.addEventListener("controllerchange", () => {
              if (!refreshing) {
                refreshing = true;
                window.location.reload();
              }
            });
          } else {
            console.warn("Service Workers require HTTPS (or localhost)");
          }
        } catch (error) {
          console.error("Service Worker registration failed:", error);
        }
      };

      // Register immediately if DOM is ready, otherwise wait for DOMContentLoaded
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", registerServiceWorker);
      } else {
        registerServiceWorker();
      }
    }

    // Listen for messages from service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "SHOW_ALERT") {
          queryClient.invalidateQueries({
            queryKey: ["notification-unread-count"],
          });
          refreshNotificationsCache(queryClient);
          setAlertMessage(event.data.data);
          setAlertOpen(true);
        }
      });
    }
  }, [queryClient]);

  const { subscribeUserToPush } = useSubscribe();

  function notifyMe() {
    if (!("Notification" in window)) {
      // Check if the browser supports notifications
      return;
    } else if (Notification.permission === "granted") {
      subscribeUserToPush();
    } else if (
      Notification.permission === "default" &&
      localStorage.getItem("notificationPermission") !== "denied"
    ) {
      setNotificationPermissionPopup(true);
    }
  }

  useEffect(() => {
    if (!alertMessage) return;
    const parsedAlertMessage = JSON.parse(alertMessage);

    if (
      alertOpen &&
      Number(parsedAlertMessage?.user_id) === Number(studentId)
    ) {
      //
      // First, try to translate the entire message as a key (for assignment_accepted, assignment_rejected, etc.)
      let translatedMessage = tCommon(parsedAlertMessage?.message);

      // If translation returns the same key (meaning translation not found), try special cases
      if (translatedMessage === parsedAlertMessage?.message) {
        // Handle [Deleted] special case
        if (parsedAlertMessage?.message?.includes("[Deleted]")) {
          translatedMessage = parsedAlertMessage?.message?.replace(
            "[Deleted]",
            `${tCommon("deleted")} - `,
          );
        } else {
          // If no translation found and no special case, use original message
          translatedMessage = parsedAlertMessage?.message;
        }
      }

      toast(translatedMessage, {
        position: "top-right",
        autoClose: 5000,
        type: "info",
        theme: "colored",
        onClose: () => setAlertOpen(false),
        onClick: () => setAlertOpen(false),
        style: {
          backgroundColor: "#276221",
          color: "white",
          fontFamily: "var(--font-montserrat)",
        },
      });
    }
  }, [alertOpen, alertMessage, tCommon]);

  useEffect(() => {
    if (role === ROLES.STUDENT && !isPreviewMode) {
      notifyMe();
    }
  }, [role, isPreviewMode]);

  if (
    isStudentDetailsLoading ||
    (!isPreviewMode && Boolean(studentDetails) && isIcaresTestStatusLoading)
  ) {
    return <Loader />;
  }

  if (isStudentDetailsError || (!isPreviewMode && isIcaresTestStatusError)) {
    const error = studentDetailsError || icaresTestStatusError;

    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-2xl font-bold">
          {error?.message || "Something went wrong"}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="w-[95%] m-auto pt-[2.5rem] pb-[2.5rem]">
        <ViewAsStudentBanner />
        <NavigationBar>
          <ScrollToTop />
          <Outlet />

          {!isPreviewMode && <OnboardingTutorial />}
        </NavigationBar>
      </div>
      {!isPreviewMode && has_skipped_tutorial && (
        <CModal
          isDialogOpen={openIcaresPopup}
          setIsDialogOpen={setOpenIcaresPopup}
          title={t("takeYourIcaresTest")}
        >
          <IcaresPopup onClose={() => setOpenIcaresPopup(false)} />
        </CModal>
      )}
      {!isPreviewMode && (
        <AllowNotifications
          notificationPermissionPopup={notificationPermissionPopup}
          setNotificationPermissionPopup={setNotificationPermissionPopup}
        />
      )}
    </>
  );
}

export default StudentLayout;
